import { Fragment, FunctionComponent, useContext } from 'react';
import { createFragmentContainer, graphql, RelayProp } from 'react-relay/legacy';
import { STEPS, STATES } from '../../constants/checkoutConstants';
import { trackCompletedStep } from '../../helpers/checkoutTracking';
import braintreeBankAccount from 'braintree-web/us-bank-account';
import pick from 'lodash.pick';
import { executeCaptcha } from 'dibs-recaptcha/exports/recaptcha';
import { billingFields, getAddressErrors } from '../../helpers/checkoutForms';
import { connect } from 'react-redux';
import { ACHFormValue } from 'dibs-ach-form/exports/ACHForm';
import achValidate from 'dibs-ach-form/exports/ACHValidate';
import CheckoutSaveAndContinueAction from '../../components/shared/actions/CheckoutSaveAndContinueAction';
import CheckoutContinueAction from '../../components/shared/actions/CheckoutContinueAction';
import CheckoutCTAButton from '../../components/shared/CheckoutCTAButton';
import RowContainer from '../../shared/CheckoutRowContainer';
import { shouldVerifyPaymentMethod } from '../../helpers/checkoutRedirects';
import { logCheckoutError } from '../../helpers/logCheckoutError';
import mandateText from 'dibs-ach-form/exports/mandateTextContinue';
import {
    togglePaymentSaving,
    bankAccountVaultedError,
    bankAccountSaved,
    achFormUpdateBlurred,
    toggleACHFormSubmissionError,
    completeWireTransfer,
    completeCheck,
    completeBankAccounts,
    toggleWireFormSubmissionError,
    completeOnPlatformWireTransfer,
} from '../../actions/checkoutPaymentActions';
import { getAvailableBankAccountPaymentMethods } from '../../helpers/checkoutPayment';
import { isVerifyingBankAccount, isAddingBankAccount } from '../../reducers/checkoutPaymentReducer';
import { getGuestUserId } from '../../helpers/checkoutGuestUserToken';
import { guestCheckoutWalletUpdater } from '../helpers/guestCheckoutWalletUpdater';
import { FormattedMessage } from 'dibs-react-intl';
import CheckoutMutationWrapper from '../../mutations/CheckoutMutationWrapper';
import { tradeFirmUpdateEoriVat } from '../../mutations/TradeFirmUpdateEoriVatMutation';
import { CheckoutServerVarsContext } from '../../components/shared/CheckoutServerVarsContext';
import { UserVaultPaymentMethodMutation } from '../../mutations/__generated__/UserVaultPaymentMethodMutation.graphql';
import styles from './styles/checkoutBankAccountsSubmit.scss';

import { CheckoutBankAccountsSubmit_viewer$data } from './__generated__/CheckoutBankAccountsSubmit_viewer.graphql';
import { CheckoutBankAccountsSubmit_buyer$data } from './__generated__/CheckoutBankAccountsSubmit_buyer.graphql';
import { CheckoutBankAccountsSubmit_transaction$data } from './__generated__/CheckoutBankAccountsSubmit_transaction.graphql';

type ArrayElement<ArrayType extends readonly unknown[]> = ArrayType[number];

export type AddressType = NonNullable<
    ArrayElement<
        NonNullable<
            NonNullable<CheckoutBankAccountsSubmit_buyer$data['addressBook']>['addressList']
        >
    >
>;

type CompleteBankAccountsType = {
    paymentMethodApiType: string;
    paymentMethodToken: string;
    paymentProcessor: string;
    lastValidBankAccountToken: string;
};

type Props = {
    viewer: CheckoutBankAccountsSubmit_viewer$data;
    buyer: CheckoutBankAccountsSubmit_buyer$data;
    transaction: CheckoutBankAccountsSubmit_transaction$data | null;
    isPaymentSaving: boolean;
    newACHForm: ACHFormValue;
    verifyBankAccountForm: ACHFormValue;
    onTogglePaymentSaving: (isSaving: boolean) => void;
    onBankAccountVaultedError: (error: string) => void;
    onACHFormUpdateBlurred: (newBlurredValues: ObjectType) => void;
    onToggleACHFormSubmissionError: (showError: boolean) => void;
    newBillingForm?: Record<string, string>;
    isBillingSameAsShipping: boolean;
    isAdding: boolean;
    shippingAddressId: string;
    isVerifying: boolean;
    relay: RelayProp;
    onBankAccountSaved: (payload: { token: string; paymentProcessor: string }) => void;
    onCompleteWireTransfer: (billingAddress?: AddressType) => void;
    onCompleteOnPlatformWireTransfer: (billingAddress?: AddressType) => void;
    onCompleteCheck: () => void;
    achShowFailedSubmissionError: boolean;
    bankAccountSectionSelected: string;
    bankAccountMethodSelected: string;
    onCompleteBankAccounts: (bankAccountPayment: CompleteBankAccountsType) => void;
    paymentMethodToken: string;
    paymentMethodApiType: string;
    lastValidBankAccountToken: string;
    shippingCountry: string;
    bankAccountState: string;
    verifyBankAccountToken: string;
    eoriNumber: string | null;
    vatId: string | null;
    onToggleWireFormSubmissionError: (showError: boolean) => void;
    wireShowFailedSubmissionError: boolean;
};

const CheckoutBankAccountsSubmit: FunctionComponent<Props> = ({
    viewer,
    isPaymentSaving,
    newACHForm,
    onTogglePaymentSaving,
    onBankAccountVaultedError,
    isAdding,
    isVerifying,
    verifyBankAccountForm,
    verifyBankAccountToken,
    buyer,
    transaction,
    relay,
    isBillingSameAsShipping,
    newBillingForm = {},
    shippingAddressId,
    onBankAccountSaved,
    onCompleteBankAccounts,
    paymentMethodToken,
    paymentMethodApiType,
    lastValidBankAccountToken,
    shippingCountry,
    onACHFormUpdateBlurred,
    onToggleACHFormSubmissionError,
    achShowFailedSubmissionError,
    bankAccountSectionSelected,
    bankAccountMethodSelected,
    onCompleteWireTransfer,
    onCompleteOnPlatformWireTransfer,
    onCompleteCheck,
    bankAccountState,
    eoriNumber,
    vatId,
    onToggleWireFormSubmissionError,
    wireShowFailedSubmissionError,
}) => {
    const availablePaymentMethodsMap = getAvailableBankAccountPaymentMethods(transaction);
    let paymentMethodsAvailable: string;
    if (
        availablePaymentMethodsMap.showACH &&
        availablePaymentMethodsMap.showCheck &&
        availablePaymentMethodsMap.showWireTransfer
    ) {
        paymentMethodsAvailable = 'achAndWireAndCheck';
    } else if (
        availablePaymentMethodsMap.showCheck &&
        availablePaymentMethodsMap.showWireTransfer
    ) {
        paymentMethodsAvailable = 'wireAndCheck';
    } else if (availablePaymentMethodsMap.showACH && availablePaymentMethodsMap.showWireTransfer) {
        paymentMethodsAvailable = 'achAndWire';
    } else if (availablePaymentMethodsMap.showACH) {
        paymentMethodsAvailable = 'ach';
    } else {
        paymentMethodsAvailable = 'wire';
    }

    const relevantForm = isVerifying ? verifyBankAccountForm : newACHForm;

    const isValid =
        bankAccountSectionSelected !== 'wireAndCheck' &&
        (paymentMethodsAvailable === 'achAndWireAndCheck' ||
            paymentMethodsAvailable === 'ach' ||
            paymentMethodsAvailable === 'achAndWire')
            ? (achValidate(relevantForm) || {}).isValid
            : true;
    const isDisabled = (!isValid || isPaymentSaving) && bankAccountState !== STATES.STATE_SAVED;
    const { isMobile } = useContext(CheckoutServerVarsContext);
    const isWireTransferPayment = bankAccountMethodSelected === 'wireTransfer';
    const isOnPlatformWireTransferEnabled =
        viewer?.stripeIsEnabled && availablePaymentMethodsMap.useOnPlatformWireTransfer;

    /**

    * On clicking continue when neither adding or verifying
     */
    const onComplete = (): void => {
        switch (bankAccountMethodSelected) {
            case 'check': {
                trackCompletedStep(STEPS.STEP_PAYMENT);
                onCompleteCheck();
                break;
            }
            case 'bankAccount': {
                if (!isDisabled) {
                    const selectedAch =
                        buyer?.wallet?.paymentMethods
                            ?.map(method => method?.paymentMethod)
                            .find(method => {
                                return (
                                    method?.paymentMethodType === 'ACH' &&
                                    method?.token === paymentMethodToken
                                );
                            }) || null;
                    trackCompletedStep(STEPS.STEP_PAYMENT);
                    onCompleteBankAccounts({
                        paymentMethodApiType,
                        paymentMethodToken,
                        lastValidBankAccountToken,
                        paymentProcessor: selectedAch?.paymentProcessor || '',
                    });
                }
            }
        }
    };

    const getCurrentShippingAddress = (): AddressType | null => {
        const addressList = buyer?.addressBook?.addressList || [];
        const address = addressList.find(a => a?.serviceId === shippingAddressId) || null;
        return address;
    };

    /**
     * On clicking continue when adding or verifying are true
     */
    const onSubmit = async (): Promise<void | null> => {
        const tradeFirmId = buyer?.tradeFirm?.serviceId;
        // get relevant billing address and validate
        const billingAddress: $TSFixMe =
            (isBillingSameAsShipping && shippingCountry === 'US' && !isVerifying) ||
            (isWireTransferPayment && isBillingSameAsShipping)
                ? pick(getCurrentShippingAddress(), billingFields)
                : newBillingForm;

        const addressErrors = getAddressErrors(billingAddress, true);

        const addressIsValid =
            (bankAccountSectionSelected !== 'wireAndCheck' &&
                (paymentMethodsAvailable === 'achAndWireAndCheck' ||
                    paymentMethodsAvailable === 'ach' ||
                    paymentMethodsAvailable === 'achAndWire')) ||
            isWireTransferPayment
                ? addressErrors.isValid
                : true;

        if (!isValid || !addressIsValid) {
            if (isWireTransferPayment) {
                onToggleWireFormSubmissionError(true);
            } else {
                onACHFormUpdateBlurred({
                    accountType: true,
                    ownershipType: true,
                    routingNumber: true,
                    accountNumber: true,
                    firstNameOnAccount: true,
                    lastNameOnAccount: true,
                    businessNameOnAccount: true,
                });
                onToggleACHFormSubmissionError(true);
            }
            return null;
        } else if (isDisabled) {
            return null;
        }

        //handle check/wire transfer scenarios
        if (
            ((paymentMethodsAvailable === 'achAndWireAndCheck' ||
                paymentMethodsAvailable === 'achAndWire') &&
                bankAccountSectionSelected === 'wireAndCheck') ||
            (paymentMethodsAvailable !== 'achAndWireAndCheck' &&
                paymentMethodsAvailable !== 'ach' &&
                paymentMethodsAvailable !== 'achAndWire')
        ) {
            if (bankAccountMethodSelected === 'wireTransfer') {
                //wireTransfer is set as the default when the state initializes
                if (tradeFirmId && (eoriNumber || vatId)) {
                    try {
                        onTogglePaymentSaving(true);
                        await tradeFirmUpdateEoriVat(relay.environment, {
                            serviceId: tradeFirmId,
                            eoriNumber,
                            vatId,
                        });
                        onTogglePaymentSaving(false);
                    } catch {
                        onTogglePaymentSaving(false);
                    }
                }
                if (isOnPlatformWireTransferEnabled) {
                    onCompleteOnPlatformWireTransfer(billingAddress);
                } else {
                    onCompleteWireTransfer(billingAddress);
                }
            } else if (bankAccountMethodSelected === 'check') {
                onCompleteCheck();
            }
            return null;
        }

        //is ach
        if (billingAddress.country !== 'US') {
            onBankAccountVaultedError('ACH_NON_US_COUNTRY');
            return null;
        }

        try {
            onTogglePaymentSaving(true);
            const clientToken = viewer?.clientToken?.value;
            const bankAccountClient = await braintreeBankAccount.create({
                authorization: clientToken,
            });
            const personalAccount = relevantForm?.ownershipType === 'personal';

            const bankDetails = {
                firstName: personalAccount ? relevantForm?.firstNameOnAccount : null,
                lastName: personalAccount ? relevantForm?.lastNameOnAccount : null,
                businessName: personalAccount ? null : relevantForm?.businessNameOnAccount,
                routingNumber: relevantForm.routingNumber,
                accountNumber: relevantForm.accountNumber,
                accountType: relevantForm.accountType,
                ownershipType: relevantForm.ownershipType,
                billingAddress: {
                    firstName: billingAddress.firstName || undefined,
                    lastName: billingAddress.lastName || undefined,
                    company: billingAddress.company || undefined,
                    streetAddress: billingAddress.line1,
                    extendedAddress: billingAddress.line2 || undefined,
                    locality: billingAddress.city,
                    region: billingAddress.state,
                    postalCode: billingAddress.zipCode,
                    countryCodeAlpha2: billingAddress.country,
                },
            };

            // format zip code if it's longer than five characters. Braintree requires a dash only for the bank account API, so we need to insert a dash after the fifth number of the zipcode
            const zipCode = bankDetails.billingAddress.postalCode;
            if (zipCode.length > 5) {
                bankDetails.billingAddress.postalCode = [
                    zipCode.slice(0, 5),
                    '-',
                    zipCode.slice(5),
                ].join('');
            }

            if (tradeFirmId && (eoriNumber || vatId)) {
                await tradeFirmUpdateEoriVat(relay.environment, {
                    serviceId: tradeFirmId,
                    eoriNumber,
                    vatId,
                });
            }

            const captchaResults = await executeCaptcha({
                action: 'checkoutVaultAch',
                featureFlag: 'vaultCreditCardCaptcha',
                featureFlagV3: 'vaultCreditCardCaptchaV3',
                relayEnvironment: relay.environment,
            });

            const braintreeData = await bankAccountClient.tokenize({
                bankDetails,
                mandateText,
            });

            const guestUserId = getGuestUserId();

            const vaultResponse =
                await CheckoutMutationWrapper.commit<UserVaultPaymentMethodMutation>(
                    'UserVaultPaymentMethodMutation',
                    relay.environment,
                    {
                        userId: buyer.serviceId || '',
                        saveToWallet: !guestUserId,
                        mockWallet: !!guestUserId,
                        paymentMethodNonce: braintreeData?.nonce,
                        verifyPaymentMethod: shouldVerifyPaymentMethod(),
                        verificationToken: isVerifying ? verifyBankAccountToken : null,
                        captchaToken: captchaResults?.captchaToken,
                        captchaKey: captchaResults?.captchaKey,
                    },
                    isMobile,
                    guestUserId
                        ? store =>
                              guestCheckoutWalletUpdater<UserVaultPaymentMethodMutation>(
                                  store,
                                  viewer.id || '',
                                  'userVaultPaymentMethod'
                              )
                        : undefined
                );
            const paymentMethod =
                vaultResponse?.userVaultPaymentMethod?.walletPaymentMethod?.paymentMethod;

            if (paymentMethod && paymentMethod.__typename === 'ACH' && paymentMethod.token) {
                const token = paymentMethod.token;
                const paymentProcessor = paymentMethod.paymentProcessor || '';
                onBankAccountSaved({ token, paymentProcessor });
                onTogglePaymentSaving(false);
            }

            trackCompletedStep(STEPS.STEP_PAYMENT);
        } catch (error: $TSFixMe) {
            logCheckoutError(error, 'ach');
            if (!error?.source) {
                //braintree-specific error
                onBankAccountVaultedError('VAULT');
            } else {
                //vaulting error. Ger error code from requestInfo.responsePayload
                const errorResponsePayload =
                    error?.source?.errors?.[0]?.requestInfo?.responsePayload;
                const { serviceResponseCode, httpCode } = errorResponsePayload || {};
                onBankAccountVaultedError(serviceResponseCode || httpCode);
            }
            onTogglePaymentSaving(false);
        }
        return null;
    };

    const onClick =
        isAdding || isVerifying || isWireTransferPayment ? () => onSubmit() : onComplete;
    const submitLabel = isAdding ? <CheckoutSaveAndContinueAction /> : <CheckoutContinueAction />;

    return (
        <Fragment>
            <CheckoutCTAButton
                onClick={onClick}
                label={submitLabel}
                isSaving={isPaymentSaving}
                marginTop
                disabled={isPaymentSaving}
                dataTn="bankAccounts-save-button"
            />
            {((achShowFailedSubmissionError && bankAccountSectionSelected !== 'wireAndCheck') ||
                (wireShowFailedSubmissionError && isWireTransferPayment)) && (
                <div className={styles.formErrorNotice}>
                    <FormattedMessage
                        id="checkout.CheckoutBankAccountForm.achFormErrorNotice"
                        defaultMessage="Please correct errors to continue."
                    />
                </div>
            )}
            <RowContainer>
                <p>{mandateText}</p>
            </RowContainer>
        </Fragment>
    );
};

type ConnectPaymentType = {
    newACHForm: ACHFormValue;
    isPaymentSaving: boolean;
    isBillingSameAsShipping: boolean;
    newBillingForm: Record<string, string>;
    achShowFailedSubmissionError: boolean;
    bankAccountMethodSelected: string;
    bankAccountSectionSelected: string;
    lastValidBankAccountToken: string;
    bankAccountState: string;
    verifyBankAccountForm: ACHFormValue;
    verifyBankAccountToken: string;
    newEoriNumber: string | null;
    newVatId: string | null;
    wireShowFailedSubmissionError: boolean;
};

type ConnectTransactionType = {
    shippingAddressId: string;
    paymentMethodToken: string;
    paymentMethodApiType: string;
};

type connectShippingType = {
    defaultCountry: string;
};

type ConnectType = {
    payment: ConnectPaymentType;
    transaction: ConnectTransactionType;
    shipping: connectShippingType;
};

const ConnectedCheckoutBankAccountsSubmit = connect(
    ({ transaction, payment, shipping }: ConnectType) => {
        return {
            achShowFailedSubmissionError: payment.achShowFailedSubmissionError,
            wireShowFailedSubmissionError: payment.wireShowFailedSubmissionError,
            shippingAddressId: transaction.shippingAddressId,
            newACHForm: payment.newACHForm,
            shippingCountry: shipping.defaultCountry,
            isPaymentSaving: payment.isPaymentSaving,
            isAdding: isAddingBankAccount(payment),
            isVerifying: isVerifyingBankAccount(payment),
            verifyBankAccountForm: payment.verifyBankAccountForm,
            isBillingSameAsShipping: payment.isBillingSameAsShipping,
            newBillingForm: payment.newBillingForm,
            bankAccountMethodSelected: payment.bankAccountMethodSelected,
            bankAccountSectionSelected: payment.bankAccountSectionSelected,
            lastValidBankAccountToken: payment.lastValidBankAccountToken,
            paymentMethodToken: transaction.paymentMethodToken,
            paymentMethodApiType: transaction.paymentMethodApiType,
            bankAccountState: payment.bankAccountState,
            verifyBankAccountToken: payment.verifyBankAccountToken,
            eoriNumber: payment.newEoriNumber,
            vatId: payment.newVatId,
        };
    },
    {
        onTogglePaymentSaving: togglePaymentSaving,
        onBankAccountVaultedError: bankAccountVaultedError,
        onBankAccountSaved: bankAccountSaved,
        onACHFormUpdateBlurred: achFormUpdateBlurred,
        onToggleACHFormSubmissionError: toggleACHFormSubmissionError,
        onToggleWireFormSubmissionError: toggleWireFormSubmissionError,
        onCompleteWireTransfer: completeWireTransfer,
        onCompleteOnPlatformWireTransfer: completeOnPlatformWireTransfer,
        onCompleteCheck: completeCheck,
        onCompleteBankAccounts: completeBankAccounts,
    }
)(CheckoutBankAccountsSubmit);

export default createFragmentContainer(ConnectedCheckoutBankAccountsSubmit, {
    viewer: graphql`
        fragment CheckoutBankAccountsSubmit_viewer on Viewer {
            id
            clientToken {
                value
            }
            stripeIsEnabled: featureFlag(feature: "stripeCheckout")
        }
    `,
    buyer: graphql`
        fragment CheckoutBankAccountsSubmit_buyer on User {
            serviceId
            addressBook {
                addressList {
                    serviceId
                    firstName
                    lastName
                    country
                    line1
                    line2
                    city
                    state
                    zipCode
                }
            }
            tradeFirm {
                serviceId
            }
            wallet {
                paymentMethods {
                    paymentMethod {
                        paymentMethodType
                        ... on ACH {
                            token
                            paymentProcessor
                        }
                    }
                }
            }
        }
    `,
    transaction: graphql`
        fragment CheckoutBankAccountsSubmit_transaction on Transaction {
            ...checkoutPayment_transaction
        }
    `,
});
